import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Calendar, Target, TrendingUp, Eye, Edit } from 'lucide-react';
import { format } from 'date-fns';

export interface Goal {
  id: string;
  employeeId: string;
  title: string;
  target: number;
  current: number;
  startDate: string;
  endDate: string;
  deadline?: string | null;
  reviewDate?: string | null;
  status: 'on-track' | 'at-risk' | 'completed';
  notes?: string | null;
  reviewFeedback?: string | null;
  changeHistory: any[];
  createdAt: string;
  updatedAt: string;
}

interface GoalCardProps {
  goal: Goal;
  onView?: (goal: Goal) => void;
  onEdit?: (goal: Goal) => void;
}

export function GoalCard({ goal, onView, onEdit }: GoalCardProps) {
  const progressPercentage = (goal.current / goal.target) * 100;

  const getStatusColor = (status: Goal['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500 text-white';
      case 'on-track':
        return 'bg-blue-500 text-white';
      case 'at-risk':
        return 'bg-yellow-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <CardTitle className="text-lg">{goal.title}</CardTitle>
            <CardDescription className="flex items-center gap-2 mt-1">
              <Target className="h-3 w-3" />
              Target: {goal.target}
            </CardDescription>
          </div>
          <Badge className={getStatusColor(goal.status)}>
            {goal.status.replace('-', ' ')}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              <span>Progress</span>
            </div>
            <span className="font-medium">
              {goal.current} / {goal.target} ({Math.round(progressPercentage)}%)
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>Start: {format(new Date(goal.startDate), 'MMM dd, yyyy')}</span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>End: {format(new Date(goal.endDate), 'MMM dd, yyyy')}</span>
          </div>
        </div>

        {goal.deadline && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>Deadline: {format(new Date(goal.deadline), 'MMM dd, yyyy')}</span>
          </div>
        )}

        {/* Notes Preview */}
        {goal.notes && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {goal.notes}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          {onView && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => onView(goal)}
            >
              <Eye className="h-4 w-4 mr-1" />
              View Details
            </Button>
          )}
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => onEdit(goal)}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
